import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { AvailabilityDay } from './utils';
import { HEADER_WIDTH } from './constants';
import { timezoneToCountryCode, countryCodeToName } from '../../../utils/timezoneToCountry';
import CountryFlag from '../../common/CountryFlag';
import { BA_TIMEZONE } from '../../../utils/timezone';

type Props = {
  availability: AvailabilityDay[];
  visibleTimeSlots: string[];
  onWheel?: (e: React.WheelEvent<HTMLDivElement>) => void;
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: () => void;
  renderDayHeader: (day: AvailabilityDay, idx: number) => React.ReactNode;
  renderAllDayEvent?: (day: AvailabilityDay, idx: number) => React.ReactNode;
  renderTimeHeader: (ts: string, rowIdx: number) => React.ReactNode;
  renderCell: (day: AvailabilityDay, dayIdx: number, ts: string, rowIdx: number) => React.ReactNode;
  insideGridChildren?: React.ReactNode;
  isSmallScreen?: boolean;
  onMount?: () => void;
  onUnmount?: () => void;
  /** Timezone IANA del psicólogo (ej: 'America/Argentina/Buenos_Aires') para mostrar bandera */
  timezone?: string;
};

const TimeGrid = forwardRef<HTMLDivElement, Props>(
  ({
    availability,
    visibleTimeSlots,
    onWheel,
    onMouseMove,
    onMouseLeave,
    renderDayHeader,
    renderAllDayEvent,
    renderTimeHeader,
    renderCell,
    insideGridChildren,
    isSmallScreen = false,
    onMount,
    onUnmount,
    timezone,
  }, ref) => {
  const tz = timezone || BA_TIMEZONE;
  const iso2 = timezoneToCountryCode(tz);
  const tzCountryName = (iso2 ? countryCodeToName(iso2) : null)?.toUpperCase() ?? 'ARGENTINA';
    const colCount = availability.length;
    const headerWidthRem = isSmallScreen ? 3.5 : HEADER_WIDTH;
    // On mobile: columns shrink to fit all 7 days on screen (Google Calendar style)
    const gridColumns = isSmallScreen
      ? `${headerWidthRem}rem repeat(${colCount}, minmax(0, 1fr))`
      : `${headerWidthRem}rem repeat(${colCount}, 1fr)`;

    useLayoutEffect(() => {
      onMount?.();
      return () => onUnmount?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // `--psi-sticky-top`: alto de la barra fija de arriba de la app mobile (la define `PwaShell`; en la web no existe y vale 0).
    // Sin ese corrimiento los días (J7 / V9…) quedaban pegados al borde de la pantalla POR ENCIMA de esa barra.
    // Mide en vivo la altura de la fila de headers (sticky top:0) para poder anclar
    // debajo suyo la fila del cartel de bloqueo ("Vacaciones & Dias Off", etc.) — sin
    // esto esa fila no era sticky y desaparecía al scrollear la grilla hacia abajo.
    const cornerCellRef = useRef<HTMLDivElement>(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    useLayoutEffect(() => {
      const el = cornerCellRef.current;
      if (!el) return;
      const measure = () => setHeaderHeight(el.getBoundingClientRect().height);
      measure();
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(el);
      return () => resizeObserver.disconnect();
    }, []);

    return (
      <div
        ref={ref}
        className={isSmallScreen ? undefined : 'calendar-week-grid'}
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: gridColumns,
          ...(isSmallScreen ? {} : {
            maxHeight: renderAllDayEvent ? "calc(37.5rem + 32px)" : "37.5rem",
            overflowY: 'scroll',
            overflowX: 'hidden',
          }),
        }}
        onWheel={onWheel}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
      >
        <div ref={cornerCellRef} style={{ position: 'sticky', top: 'var(--psi-sticky-top, 0px)', background: '#ffffff', borderRight: '1px solid rgba(203, 213, 225, 0.3)', borderBottom: '1px solid rgba(203, 213, 225, 0.3)', display: 'grid', placeItems: 'center', padding: '0.25rem', zIndex: 20 }}>
          {!isSmallScreen && (
            <span data-testid="huso-horario-label" style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 500, letterSpacing: '0.025em', lineHeight: 1.3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem' }}>
              <span>Huso horario:</span>
              <span style={{ whiteSpace: 'nowrap' }}>{iso2 && <><CountryFlag iso2={iso2} size="0.65rem" /> </>}{tzCountryName}</span>
            </span>
          )}
        </div>
        {availability.map((day, idx) => renderDayHeader(day, idx))}
        {renderAllDayEvent && (
          <>
            {/* zIndex por encima de SessionItem (35/36) — sin esto una sesión que scrollea
                hasta esta banda se pinta encima del cartel de bloqueo y lo tapa. */}
            <div style={{ position: 'sticky', top: `calc(var(--psi-sticky-top, 0px) + ${headerHeight}px)`, zIndex: 45, background: '#ffffff', borderRight: '1px solid rgba(203, 213, 225, 0.3)', borderBottom: '1px solid rgba(203, 213, 225, 0.3)', height: 32 }} />
            {availability.map((day, idx) => (
              <div
                key={`allday-${day.date}`}
                style={{ position: 'sticky', top: `calc(var(--psi-sticky-top, 0px) + ${headerHeight}px)`, zIndex: 45, background: '#ffffff', height: 32, overflow: 'hidden', borderBottom: '1px solid rgba(203, 213, 225, 0.3)' }}
              >
                {renderAllDayEvent(day, idx)}
              </div>
            ))}
          </>
        )}
        {visibleTimeSlots.map((ts, rowIdx) => (
          <React.Fragment key={`row-${ts}`}>
            {renderTimeHeader(ts, rowIdx)}
            {availability.map((day, idx) => (
              <React.Fragment key={`${day.date}-${ts}`}>{renderCell(day, idx, ts, rowIdx)}</React.Fragment>
            ))}
          </React.Fragment>
        ))}
        {insideGridChildren}
      </div>
    );
  }
);

TimeGrid.displayName = 'TimeGrid';

export default TimeGrid;


